import { ENV } from '../config/env';
import { RazorpayOptions, PaymentData, CreateOrderResponse, RazorpayResponse, CreateOrderRequest, CreateOrderApiResponse, VerifyPaymentApiResponse } from '../types/razorpay';
import { showPaymentSuccess, showPaymentError, showPaymentDismissed } from '../utils/paymentNotifications';
import { httpService } from './httpService';
import { store } from '../redux/store/store';
import { UserDetails } from '../features/auth/types';
import { decryptResponse, encryptResponse } from './encrypt';
import { getSessionKey } from './sessionKey';
import { REQ_ENC_KEY } from './api';

class RazorpayService {
  private readonly keyId: string;
  private readonly keySecret: string;
  private currentPaymentData: PaymentData | null = null;
  private currentOrgId: string | null = null;
  private currentAppId: string | null = null;
  private currentOrderData: CreateOrderResponse | null = null;

  constructor() {
    this.keyId = ENV.RAZORPAY_KEY_ID || 'rzp_test_RM9tlQccfJJohY';
    this.keySecret = ENV.RAZORPAY_KEY_SECRET || '';
  }

  /**
   * Load Razorpay script dynamically
   */
  private loadRazorpayScript(): Promise<boolean> {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        console.log('Razorpay script already loaded');
        resolve(true);
        return;
      }

      // Check if script is already being loaded
      const existingScript = document.querySelector('script[src*="checkout.razorpay.com"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(true));
        existingScript.addEventListener('error', () => resolve(false));
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => {
        console.log('Razorpay script loaded successfully');
        resolve(true);
      };
      script.onerror = (error) => {
        resolve(false);
      };
      document.head.appendChild(script);
    });
  }

  /**
   * Convert amount to smallest currency unit (paise for INR, cents for USD)
   * Razorpay requires amounts in the smallest currency unit
   */
  private convertToSmallestUnit(amount: number, currency: string): number {
    // First, round the amount to 2 decimal places (e.g., 349.234 → 349.23)
    const roundedAmount = Math.round(amount * 100) / 100;
    // Then convert to smallest unit (e.g., 349.23 → 34923)
    return roundedAmount;
  }

  /**
   * Create payment order using backend API
   */
  private async createOrder(paymentData: PaymentData, organizationId: string): Promise<CreateOrderResponse> {
    try {
      // Generate a short receipt ID (max 40 chars) using timestamp and first 8 chars of planId
      const timestamp = Date.now().toString().slice(-10); // Last 10 digits of timestamp
      const shortPlanId = paymentData.planId.substring(0, 8); // First 8 chars of planId
      const receipt = `rcpt_${shortPlanId}_${timestamp}`; // Format: rcpt_XXXXXXXX_YYYYYYYYYY (max 28 chars)

      const requestData: CreateOrderRequest = {
        organization_id: organizationId,
        amount: paymentData.amount, // Convert to paise
        currency: paymentData.currency,
        receipt: receipt,
        plan_details: paymentData
      };

      const response = await httpService.post<any>(
        '/user/createRazorpayOrder',
        { data: await encryptResponse(requestData, REQ_ENC_KEY) }
      );

      if (response.data.status !== 200) {
        throw new Error(response.data.message || 'Failed to create order');
      }

      if (response.data.error) {
        throw new Error(response.data.error.message || 'API returned an error');
      }
      const data = response.data.data;
      const decryptedData : any = await decryptResponse(data, getSessionKey() as string);
      const razorpayOrder = decryptedData.razorpayOrder;
      return {
        id: razorpayOrder.id,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        receipt: razorpayOrder.receipt
      };
    } catch (error: any) {

      // Handle different types of errors
      let errorMessage = 'Failed to create payment order';

      if (error.response?.data) {
        // API returned an error response
        const apiError = error.response.data;
        errorMessage = apiError.message || apiError.error?.message || errorMessage;
      } else if (error.message) {
        // Network or other error
        errorMessage = error.message;
      }

      throw new Error(errorMessage);
    }
  }

  /**
   * Initialize Razorpay payment
   */
  async initiatePayment(paymentData: PaymentData, organizationId?: string, appId?: string): Promise<void> {
    const orgId = organizationId || paymentData.organizationId || this.getOrganizationId();
    const resolvedAppId = appId || paymentData.appId || null;
    // Store payment data for later use in verification
    this.currentPaymentData = paymentData;
    this.currentOrgId = orgId;
    this.currentAppId = resolvedAppId;
    try {
      // Load Razorpay script
      const isScriptLoaded = await this.loadRazorpayScript();
      if (!isScriptLoaded) {
        throw new Error('Failed to load Razorpay script');
      }

      // Convert amount to smallest currency unit (paise for INR, cents for USD)
      const amountInSmallestUnit = this.convertToSmallestUnit(
        paymentData.amount,
        paymentData.currency
      );
      console.log("Amtount in smallest unit:", amountInSmallestUnit);
      // Create order with original currency
      const orderData = await this.createOrder({
        ...paymentData,
        amount: amountInSmallestUnit,
        currency: paymentData.currency
      }, orgId);

      // Store order data for later use
      this.currentOrderData = orderData;

      // Razorpay options
      const options: RazorpayOptions = {
        key: this.keyId,
        amount: orderData.amount,
        currency: orderData.currency, // Use the currency from order (INR or USD)
        name: 'Sampark',
        description: `${paymentData.planName} Plan - ${paymentData.billingCycle}`,
        order_id: orderData.id,
        handler: this.handlePaymentSuccess.bind(this),
        prefill: {
          name: '',
          email: '',
          contact: ''
        },
        notes: {
          plan_id: paymentData.planId,
          billing_cycle: paymentData.billingCycle
        },
        theme: {
          color: '#1976d2'
        },
        modal: {
          ondismiss: this.handlePaymentDismiss.bind(this)
        }
      };

      // Debug logging for troubleshooting
      console.log('Razorpay Options:', {
        key: this.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        order_id: orderData.id,
        name: 'Sampark'
      });

      // Validate options before proceeding
      if (!this.validateRazorpayOptions(options)) {
        throw new Error('Invalid Razorpay options. Please check the console for details.');
      }

      // Create Razorpay instance and open
      const razorpay = new window.Razorpay(options);
      
      // Add error handler for Razorpay failures
      razorpay.on('payment.failed', (response: any) => {
        this.handlePaymentFailure(response.error, response);
      });
      
      razorpay.open();

    } catch (error) {
      
      // Store failed payment attempt with full error data
      // if (this.currentPaymentData && this.currentOrgId) {
      //   await this.storePaymentAttempt(
      //     this.currentPaymentData,
      //     this.currentOrgId,
      //     'failed',
      //     undefined,
      //     error, // Pass full error object
      //     this.currentOrderData?.receipt
      //   );
      // }

      const errorMessage = error instanceof Error ? error.message : 'Payment initialization failed';
      showPaymentError(errorMessage);
      throw error;
    }
  }

  /**
   * Handle successful payment
   */
  private async handlePaymentSuccess(response: RazorpayResponse): Promise<void> {
    console.log('Payment successful:', response);

    if (!this.currentPaymentData || !this.currentOrgId) {
      showPaymentError('Payment data missing. Please contact support.');
      return;
    }

    try {
      // First, store the successful payment attempt
      // await this.storePaymentAttempt(
      //   this.currentPaymentData,
      //   this.currentOrgId,
      //   'success',
      //   response,
      //   undefined,
      //   this.currentOrderData?.receipt
      // );

      // Verify the payment signature and update database
      const isVerified = await this.verifyPayment(
        response.razorpay_payment_id,
        response.razorpay_order_id || '',
        response.razorpay_signature || '',
        this.currentPaymentData,
        this.currentOrgId
      );

      if (isVerified) {
        // Show success notification with actual plan details
        showPaymentSuccess(this.currentPaymentData.planName, this.currentPaymentData.billingCycle);

        await this.storePaymentAttempt(
          this.currentPaymentData,
          this.currentOrgId,
          'success',
          response,
          undefined,
          this.currentOrderData?.receipt
        );

        // Emit success event for other components to listen
        window.dispatchEvent(new CustomEvent('paymentSuccess', {
          detail: {
            ...response,
            verified: true,
            planData: this.currentPaymentData
          }
        }));

        // Clear stored data
        this.currentPaymentData = null;
        this.currentOrgId = null;
        this.currentOrderData = null;
      } else {
        // Payment verification failed - store as failed
        // await this.storePaymentAttempt(
        //   this.currentPaymentData,
        //   this.currentOrgId,
        //   'failed',
        //   response,
        //   { type: 'verification_failed', message: 'Payment verification failed', response: response },
        //   this.currentOrderData?.receipt
        // );

        showPaymentError('Payment verification failed. Please contact support.');

        window.dispatchEvent(new CustomEvent('paymentVerificationFailed', {
          detail: response
        }));
      }
    } catch (error) {

      // Store the error in database
      // if (this.currentPaymentData && this.currentOrgId) {
      //   await this.storePaymentAttempt(
      //     this.currentPaymentData,
      //     this.currentOrgId,
      //     'failed',
      //     response,
      //     error, // Pass full error object
      //     this.currentOrderData?.receipt
      //   );
      // }

      showPaymentError('Payment completed but verification failed. Please contact support.');

      window.dispatchEvent(new CustomEvent('paymentVerificationError', {
        detail: { response, error }
      }));
    }
  }

  /**
   * Handle payment dismissal/cancellation
   */
  private async handlePaymentDismiss(): Promise<void> {
    console.log('Payment dismissed by user');

    // Store the dismissed payment attempt
    if (this.currentPaymentData && this.currentOrgId) {
      await this.storePaymentAttempt(
        this.currentPaymentData,
        this.currentOrgId,
        'failed',
        undefined,
        { type: 'user_dismissal', message: 'Payment dismissed by user', timestamp: new Date().toISOString() },
        this.currentOrderData?.receipt
      );
    }

    // Show dismissal notification
    showPaymentDismissed();

    // Clear stored data
    this.currentPaymentData = null;
    this.currentOrgId = null;

    // You might want to emit a custom event or call a callback
    window.dispatchEvent(new CustomEvent('paymentDismissed'));
  }

  /**
   * Handle Razorpay payment failures
   */
  private async handlePaymentFailure(error: any, razorpayResponse?: any): Promise<void> {
    console.log('Razorpay failure response:', razorpayResponse);

    // Store the failed payment attempt with Razorpay response data
    if (this.currentPaymentData && this.currentOrgId) {
      await this.storePaymentAttempt(
        this.currentPaymentData,
        this.currentOrgId,
        'failed',
        razorpayResponse, // Pass the full response which contains payment_id, order_id, signature
        error, // Pass the full error object instead of just message
        this.currentOrderData?.receipt
      );
    }

    // Show error notification
    showPaymentError(`Payment failed: ${error.description || 'Please try again'}`);

    // Clear stored data
    // this.currentPaymentData = null;
    // this.currentOrgId = null;
    // this.currentOrderData = null;

    // Emit failure event
    window.dispatchEvent(new CustomEvent('paymentFailed', {
      detail: error
    }));
  }

  /**
   * Get organization ID from Redux store or user context
   */
  private getOrganizationId(): string {
    try {
      const state = store.getState();
      const userDetails = state.auth?.userDetails as UserDetails;
      return userDetails?.organization_id || 'default_org';
    } catch (error) {
      console.warn('Could not get organization ID from store:', error);
      return 'default_org';
    }
  }

  /**
   * Validate Razorpay options before creating instance
   */
  private validateRazorpayOptions(options: RazorpayOptions): boolean {
    const errors: string[] = [];

    // Validate key
    if (!options.key || !options.key.startsWith('rzp_')) {
      errors.push(`Invalid key: ${options.key}`);
    }

    // Validate amount
    if (!options.amount || options.amount <= 0) {
      errors.push(`Invalid amount: ${options.amount}`);
    }

    // Validate currency (Razorpay supports INR and USD)
    if (options.currency !== 'INR' && options.currency !== 'USD') {
      errors.push(`Invalid currency: ${options.currency}. Only INR and USD are supported.`);
    }

    // Validate order_id
    if (!options.order_id || options.order_id.length === 0) {
      errors.push(`Invalid order_id: ${options.order_id}`);
    }

    // Validate name
    if (!options.name || options.name.length === 0) {
      errors.push(`Invalid name: ${options.name}`);
    }

    const isValid = errors.length === 0;

    if (!isValid) {
    } else {
      console.log('Razorpay options validation passed ✓');
    }

    return isValid;
  }

  /**
   * Store payment attempt in database (both success and failure)
   */
  private async storePaymentAttempt(
    paymentData: PaymentData,
    organizationId: string,
    status: 'success' | 'failed' | 'pending',
    razorpayResponse?: RazorpayResponse,
    errorData?: any,
    receiptId?: string
  ): Promise<void> {
    try {
      console.log(`Storing payment attempt with status: ${status}`, razorpayResponse);
      
      // Extract error message from error data for backward compatibility
      let errorMessage = null;
      if (errorData) {
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.description) {
          errorMessage = errorData.description;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.reason) {
          errorMessage = errorData.reason;
        } else {
          errorMessage = JSON.stringify(errorData);
        }
      }
      
      const paymentRecord = {
        organization_id: this.currentOrgId,
        app_id: this.currentAppId,
        plan_id: paymentData.planId,
        plan_name: paymentData.planName,
        billing_cycle: paymentData.billingCycle,
        amount: paymentData.amount,
        currency: paymentData.currency,
        status: status,
        razorpay_payment_id: razorpayResponse?.razorpay_payment_id || errorData?.metadata?.payment_id || null,
        razorpay_order_id: razorpayResponse?.razorpay_order_id || errorData?.metadata?.order_id || null,
        razorpay_signature: razorpayResponse?.razorpay_signature || null,
        receipt_id: receiptId || null,
        error_message: errorMessage,
        error_data: errorData ? errorData : null,
        attempt_timestamp: new Date().toISOString(),
        user_tries: paymentData.userLength || ''
      };

      await httpService.post('/user/storePaymentAttempt', {
        data: await encryptResponse(paymentRecord, REQ_ENC_KEY)
      });

      console.log(`Payment attempt stored: ${status}`);
    } catch (error) {
      // Don't throw error as this shouldn't break the payment flow
    }
  }

  /**
   * Verify payment and update database
   */
  async verifyPayment(
    paymentId: string,
    orderId: string,
    signature: string,
    paymentData?: PaymentData,
    organizationId?: string
  ): Promise<boolean> {
    try {
      // Prepare verification data
      const verificationData: any = {
        razorpay_payment_id: paymentId,
        razorpay_order_id: orderId,
        razorpay_signature: signature
      };

      // Include payment details for database update if available
      if (paymentData && organizationId) {
        verificationData.organization_id = organizationId;
        verificationData.plan_id = paymentData.planId;
        verificationData.plan_name = paymentData.planName;
        verificationData.billing_cycle = paymentData.billingCycle;
        verificationData.amount = paymentData.amount;
        verificationData.currency = paymentData.currency;
      }

      // Call your backend verification endpoint
      const response = await httpService.post<VerifyPaymentApiResponse>('/user/verifyRazorpayPayment', {
        data: await encryptResponse(verificationData, REQ_ENC_KEY)
      });

      return response.data.status === 200 && !response.data.error;
    } catch (error: any) {

      // Log detailed error for debugging
      if (error.response?.data) {
      }

      return false;
    }
  }
}

export default new RazorpayService();